import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Small header pill showing kill-switch state with 1-tap toggle. Polls every
 * 30s so the state stays in sync across tabs / sessions.
 */
export default function KillSwitchButton() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/risk/prefs");
      setEnabled(!!data.prefs?.kill_switch);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const toggle = async (e) => {
    e.stopPropagation();
    if (loading) return;
    // If OFF, ask for confirmation before halting
    if (!enabled) {
      const ok = window.confirm(
        "STOP EVERYTHING?\n\nThis halts every bot and rejects every new order across your account. Existing positions stay open."
      );
      if (!ok) return;
    }
    setLoading(true);
    try {
      await api.post(`/risk/kill-switch?enabled=${!enabled}`);
      const next = !enabled;
      setEnabled(next);
      if (next) toast.warning("Kill switch ON — bots halted");
      else toast.success("Kill switch OFF — bots may resume");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't toggle kill switch");
    }
    setLoading(false);
  };

  return (
    <button
      onClick={toggle}
      onContextMenu={(e) => { e.preventDefault(); navigate("/risk-settings"); }}
      data-testid="header-kill-switch"
      title={enabled ? "Kill switch is ON — click to resume trading" : "Emergency stop all bots"}
      className={`flex items-center gap-1.5 h-8 px-2.5 rounded-full text-[11px] font-mono-tab uppercase tracking-wider transition-colors border ${
        enabled
          ? "bg-red-500/20 text-red-300 border-red-500/40 hover:bg-red-500/30 animate-pulse"
          : "bg-secondary/40 text-muted-foreground border-border hover:text-foreground hover:border-red-500/40"
      }`}
    >
      {enabled ? <ShieldAlert className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
      <span className="hidden sm:inline">{enabled ? "HALTED" : "Kill"}</span>
    </button>
  );
}
